import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';

import { apiErrorMessage, apiRequestId, isCartExpiredError } from './api-error';

const DEFAULT_DURATION = 5000;

/** Long enough to read a request id off the bar and copy it into a support report. */
const API_ERROR_DURATION = 10000;

/**
 * The one place a checkout failure is said out loud. Every step that can be refused by the backend
 * goes through here, so a refusal always produces a message rather than a page that quietly does
 * nothing (PAUL-3197).
 */
@Injectable({ providedIn: 'root' })
export class ErrorSnackbarService {
  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translocoService: TranslocoService
  ) {}

  /**
   * Says what the backend said and, when it gave one, the id it logged the failure under: without it
   * a report ("paying did not work") is nothing a backend developer can find in the logs.
   *
   * `message` overrides the backend's own text for the callers that already know better what to say;
   * the request id is still appended, because that is the half the report needs either way.
   */
  showApiError(err: unknown, message?: string): void {
    // An expired cart is answered by the checkout wrapper's unclosable notice, which drops the cart and
    // restarts the flow (see CartExpiredInterceptor). A bar underneath it would say the same thing twice
    // and outlive the reload it triggers, so this stays quiet — including for the callers that pass a
    // `message` of their own, whose advice ("enter a card one step back") no longer applies to a cart
    // that is gone.
    if (isCartExpiredError(err)) {
      return;
    }
    const text = message ?? apiErrorMessage(this.translocoService, err);
    const requestId = apiRequestId(err);
    this.show(
      requestId ? this.translocoService.translate('errors.with_request_id', { message: text, id: requestId }) : text,
      API_ERROR_DURATION
    );
  }

  /** A message this app worded itself — nothing came back from the server to reference. */
  show(message: string, duration: number = DEFAULT_DURATION): void {
    this.snackBar.open(message, this.translocoService.translate('global.close'), { duration });
  }
}
